import React, { createContext, useContext, useMemo } from "react";

import type { ServerCmsTypesProductSummary as ProductSummary } from "@reactivated";

import { ReactivatedContext } from "../../../context";
import { SKU, skuCatalog } from "../engine/schema";
import { buildMergedCatalog } from "./merge";
import type { MergedProduct } from "./merge";
import { useProductCatalog } from "./useProductCatalog";

type ProductFetchState = "loading" | "error" | "ready";

type ProductContextValue = {
    fetchState: ProductFetchState;
    retry: () => void;
    /** Active locale + currency, used by components that format derived totals. */
    locale: string;
    currency: string;
    /** Always-complete merged catalog, keyed by every schema SKU. */
    catalog: Map<SKU, MergedProduct>;
    /**
     * Convenience getter — equivalent to `catalog.get(sku)` but typed
     * as always-defined since every schema SKU has a row.
     */
    getProduct: (sku: SKU) => MergedProduct;
    /**
     * True when at least one catalog row reflects CMS data. False while
     * the fetch is loading, on error, when no provider is mounted, or
     * when the API returned no matching products — i.e., whenever the
     * catalog rows are entirely schema-fallback values. Consumers that
     * send catalog data off-site should branch on this rather than
     * trusting the fallbacks to be current.
     */
    hasCmsData: boolean;
};

const buildContextValue = (
    products: ProductSummary[] | null,
    fetchState: ProductFetchState,
    locale: string,
    currency: string,
    retry: () => void = () => {},
): ProductContextValue => {
    const catalog = buildMergedCatalog(products, locale, currency);
    return {
        fetchState,
        retry,
        locale,
        currency,
        catalog,
        getProduct: (sku) => {
            const product = catalog.get(sku);
            if (!product) {
                throw new Error(
                    `ProductContext: no catalog entry for SKU "${sku}"`,
                );
            }
            return product;
        },
        hasCmsData:
            products !== null && products.some((p) => p.sku in skuCatalog),
    };
};

const defaultValue: ProductContextValue = buildContextValue(
    null,
    "ready",
    "en-us",
    "USD",
);

const ProductContext = createContext<ProductContextValue>(defaultValue);

export const ProductContextProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const reactivated = useContext(ReactivatedContext);
    const locale = reactivated.i18n.language.code;

    const { fetchState, retry } = useProductCatalog(locale);
    const products = fetchState.status === "ready" ? fetchState.products : null;
    // Pull currency from the first API row that has pricing data; falls
    // back to USD only if no row has msrp at all.
    const currency =
        products?.find((p) => p.msrp)?.msrp?.currency.code ?? "USD";

    const value = useMemo(
        () =>
            buildContextValue(
                products,
                fetchState.status,
                locale,
                currency,
                retry,
            ),
        [products, fetchState.status, locale, currency, retry],
    );

    return (
        <ProductContext.Provider value={value}>
            {children}
        </ProductContext.Provider>
    );
};

export const MockProductContextProvider = ({
    products = [],
    fetchState = "ready",
    locale = "en-us",
    currency = "USD",
    children,
}: {
    products?: ProductSummary[];
    fetchState?: ProductFetchState;
    locale?: string;
    currency?: string;
    children: React.ReactNode;
}) => {
    const value = useMemo(
        () =>
            buildContextValue(
                fetchState === "ready" ? products : null,
                fetchState,
                locale,
                currency,
            ),
        [products, fetchState, locale, currency],
    );
    return (
        <ProductContext.Provider value={value}>
            {children}
        </ProductContext.Provider>
    );
};

export const useProductContext = (): ProductContextValue =>
    useContext(ProductContext);
